import { useEffect, useRef, useState } from "react";
import {
  ActionIcon, Alert, Badge, Box, Button, Group, Modal, NumberInput, Paper, Stack, Table, Text,
} from "@mantine/core";
import { IconMinus, IconPlus } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../../api/client";
import type { StockItem } from "../../api/types";
import { BarcodeScanner } from "../BarcodeScanner";
import { fmtQty, isWholeUnit, roundQty, validQty } from "./qty";

export interface ScanListRow {
  stock_item_id: number;
  name: string;
  unit: string;
  qty: number | "";
}

export interface ScanQtyOverlayProps {
  opened: boolean;
  onClose: () => void;
  /** Lista curentă (afișată sub cameră). */
  rows: ScanListRow[];
  /** Maximul total permis pentru articol (stoc la predare; la retur — cât e la destinatar). */
  limitFor: (item: StockItem) => number;
  /** Cantitatea deja în listă pentru articol. */
  inListFor: (stockItemId: number) => number;
  /** Adaugă (adună) la linia existentă. `item` vine proaspăt de la server. */
  onAdd: (item: StockItem, qty: number) => void;
  /** Textul „disponibil” de sub nume (implicit „în stoc: N u.m.”). */
  limitLabel?: (item: StockItem, limit: number) => string;
  /** Mesajul la depășirea limitei (implicit „Stoc insuficient…”). */
  limitError?: (item: StockItem, limit: number) => string;
}

const STK_RE = /^\s*STK:\d{1,9}\s*$/i;

type Pending = { item: StockItem; limit: number; max: number };

/**
 * Cameră pe tot ecranul; la detectarea unui cod `STK:<id>` apare peste cameră
 * alegerea cantității. Cât timp e deschisă, alte detectări sunt ignorate.
 * Camera rămâne pornită pentru următorul articol.
 */
export function ScanQtyOverlay({
  opened, onClose, rows, limitFor, inListFor, onAdd, limitLabel, limitError,
}: ScanQtyOverlayProps) {
  const { t } = useTranslation();
  const labelOf = (item: StockItem, limit: number) =>
    limitLabel ? limitLabel(item, limit) : t("freeIssues.inStock", { qty: fmtQty(item.quantity), unit: item.unit });
  const errorOf = (item: StockItem, limit: number) =>
    limitError ? limitError(item, limit) : t("freeIssues.insufficient", { qty: fmtQty(limit), unit: item.unit });
  const [pending, setPending] = useState<Pending | null>(null);
  const [qty, setQty] = useState<number | "">(1);
  const [msg, setMsg] = useState<{ color: string; text: string } | null>(null);
  const busyRef = useRef(false);
  const msgTimer = useRef<number | null>(null);
  // Crește la fiecare deschidere/închidere — rezultatele întârziate ale unei
  // căutări pornite înainte de închidere sunt ignorate.
  const sessionRef = useRef(0);

  useEffect(() => {
    sessionRef.current += 1;
    if (!opened) {
      setPending(null);
      setMsg(null);
      busyRef.current = false;
    }
  }, [opened]);
  useEffect(() => () => { if (msgTimer.current) window.clearTimeout(msgTimer.current); }, []);

  function flash(color: string, text: string) {
    setMsg({ color, text });
    if (msgTimer.current) window.clearTimeout(msgTimer.current);
    msgTimer.current = window.setTimeout(() => setMsg(null), 3000);
  }

  async function handleDetect(raw: string) {
    if (busyRef.current) return;
    // Doar coduri STK:<id>; restul (DevEUI, link de pașaport) — direct „Cod necunoscut”.
    if (!STK_RE.test(raw)) {
      flash("red", t("freeIssues.scan.unknownCode"));
      return;
    }
    busyRef.current = true;
    const session = sessionRef.current;
    try {
      const { item } = await api.stockItemByCode(raw);
      if (session !== sessionRef.current) return;
      const limit = limitFor(item);
      const max = roundQty(Math.max(0, limit - inListFor(item.id)));
      setPending({ item, limit, max });
      setQty(max >= 1 || isWholeUnit(item.unit) ? 1 : max);
      setMsg(null);
    } catch (e) {
      if (session !== sessionRef.current) return;
      const code = e instanceof ApiError ? (e.detail as { error?: string } | null)?.error : undefined;
      if (code === "unknown_code" || (e instanceof ApiError && e.status === 404)) flash("red", t("freeIssues.scan.unknownCode"));
      else if (code === "inactive_item") flash("orange", t("freeIssues.scan.inactiveItem"));
      else flash("red", e instanceof ApiError ? t("common.error") : String(e));
      busyRef.current = false;
    }
  }

  function closePending() {
    setPending(null);
    busyRef.current = false;
  }

  function confirmAdd() {
    if (!pending || !validQty(qty, pending.item.unit) || qty > pending.max) return;
    onAdd(pending.item, roundQty(qty));
    flash("green", t("freeIssues.scan.added", { name: pending.item.name, qty: fmtQty(qty), unit: pending.item.unit }));
    closePending();
  }

  const whole = pending ? isWholeUnit(pending.item.unit) : true;
  const overMax = pending != null && typeof qty === "number" && qty > pending.max;
  const canAdd = pending != null && pending.max > 0 && validQty(qty, pending.item.unit) && !overMax;
  const step = (d: number) => {
    if (!pending) return;
    const cur = typeof qty === "number" ? qty : 0;
    const next = roundQty(Math.min(pending.max, Math.max(whole ? 1 : 0, cur + d)));
    setQty(next);
  };

  return (
    <Modal opened={opened} onClose={onClose} fullScreen zIndex={300} title={t("freeIssues.scan.title")} padding="sm">
      <Stack gap="sm" maw={640} mx="auto">
        <Box pos="relative">
          <BarcodeScanner active={opened} onDetect={(raw) => void handleDetect(raw)} dedupeMs={2000} />
          {msg && !pending && (
            <Alert color={msg.color} variant="filled" pos="absolute" top={8} left={8} right={8} p="xs">
              {msg.text}
            </Alert>
          )}
          {pending && (
            <Paper
              pos="absolute" left={8} right={8} bottom={8} p="sm" radius="md" shadow="md" withBorder
              style={{ background: "var(--mantine-color-body)", opacity: 0.97 }}
            >
              <Stack gap={6}>
                <Text fw={700} lineClamp={2}>{pending.item.name}</Text>
                <Text size="sm" c="dimmed">
                  {labelOf(pending.item, pending.limit)}
                  {inListFor(pending.item.id) > 0 && ` · ${t("freeIssues.scan.inList", { qty: fmtQty(inListFor(pending.item.id)) })}`}
                </Text>
                {pending.max <= 0 ? (
                  <Text size="sm" c="red">{errorOf(pending.item, pending.limit)}</Text>
                ) : (
                  <Group gap="xs" wrap="nowrap">
                    <ActionIcon size="xl" variant="default" onClick={() => step(-1)} aria-label="-"><IconMinus size={20} /></ActionIcon>
                    <NumberInput
                      size="md"
                      style={{ flex: 1 }}
                      value={qty}
                      onChange={(v) => setQty(v === "" ? "" : Number(v))}
                      min={0}
                      max={pending.max}
                      allowNegative={false}
                      allowDecimal={!whole}
                      decimalScale={whole ? 0 : 4}
                      hideControls
                      inputMode={whole ? "numeric" : "decimal"}
                      rightSection={<Text size="xs" c="dimmed" pr={6}>{pending.item.unit}</Text>}
                      error={overMax ? errorOf(pending.item, pending.limit) : undefined}
                    />
                    <ActionIcon size="xl" variant="default" onClick={() => step(1)} aria-label="+"><IconPlus size={20} /></ActionIcon>
                  </Group>
                )}
                <Group justify="flex-end" gap="xs">
                  <Button variant="default" onClick={closePending}>{t("common.cancel")}</Button>
                  <Button onClick={confirmAdd} disabled={!canAdd}>{t("common.add")}</Button>
                </Group>
              </Stack>
            </Paper>
          )}
        </Box>
        <Text size="xs" c="dimmed">{t("freeIssues.scan.hint")}</Text>
        {rows.length > 0 && (
          <Table withTableBorder striped>
            <Table.Tbody>
              {rows.map((r) => (
                <Table.Tr key={r.stock_item_id}>
                  <Table.Td>{r.name}</Table.Td>
                  <Table.Td ta="right" style={{ whiteSpace: "nowrap" }}>
                    <Badge variant="light">{r.qty === "" ? "—" : fmtQty(r.qty)} {r.unit}</Badge>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
        <Button onClick={onClose} fullWidth>{t("freeIssues.scan.done")}</Button>
      </Stack>
    </Modal>
  );
}
